import { useState, useEffect, useCallback } from 'react';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Truck, Package, CheckCircle2, XCircle, AlertTriangle, Eye, FileText,
  Calendar, Bell, MessageSquare, Route, ArrowRight, Loader2, DollarSign, Settings,
} from 'lucide-react';

const PAYMENT_STATUS_MAP = {
  pending_transfer: { label: 'Pendiente', color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20' },
  receipt_uploaded: { label: 'Comprobante subido', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  under_review: { label: 'En revision', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  payment_verified: { label: 'Pago verificado', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  payment_not_found: { label: 'Pago no encontrado', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
  invalid_receipt: { label: 'Comprobante invalido', color: 'bg-red-500/10 text-red-400 border-red-500/20' },
  suspected_fraud: { label: 'Posible estafa', color: 'bg-red-600/10 text-red-500 border-red-600/20' },
  route_ready: { label: 'Listo para ruta', color: 'bg-emerald-600/10 text-emerald-300 border-emerald-600/20' },
  shipped: { label: 'Enviado', color: 'bg-teal-500/10 text-teal-400 border-teal-500/20' },
  canceled: { label: 'Cancelado', color: 'bg-zinc-600/10 text-zinc-500 border-zinc-600/20' },
};

export default function EnbixosOperatorPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tab, setTab] = useState('requests');
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [routes, setRoutes] = useState([]);
  const [routeForm, setRouteForm] = useState({ scheduled_date: '', scheduled_time: '', notes: '', branches_included: [], active: true });
  const [config, setConfig] = useState(null);
  const [configEditing, setConfigEditing] = useState(false);
  const [notifications, setNotifications] = useState({ notifications: [], unread_count: 0 });
  const [selectedForRoute, setSelectedForRoute] = useState([]);
  const [routeAssignDate, setRouteAssignDate] = useState('');

  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.payment_status = filter;
      const data = await api.getShippingRequests(params);
      setRequests(data.requests || []);
      setStats(data.stats || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { loadRequests(); }, [loadRequests]);
  useEffect(() => {
    if (tab === 'routes') api.getEnbixosRoutes().then(setRoutes).catch(() => {});
    if (tab === 'config' && isAdmin) api.getEnbixosConfig().then(setConfig).catch(() => {});
    if (tab === 'notifications') api.getEnbixosNotifications().then(setNotifications).catch(() => {});
  }, [tab, isAdmin]);

  const openDetail = async (id) => {
    try {
      const d = await api.getShippingRequest(id);
      setDetail(d);
      setDetailId(id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleStatusChange = async (requestId, paymentStatus, note) => {
    try {
      await api.updateShippingStatus(requestId, { payment_status: paymentStatus, note });
      toast.success('Estado actualizado');
      loadRequests();
      if (detailId === requestId) openDetail(requestId);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !detailId) return;
    try {
      await api.addShippingNote(detailId, { text: noteText });
      toast.success('Nota agregada');
      setNoteText('');
      openDetail(detailId);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleAssignRoute = async () => {
    if (!selectedForRoute.length) return;
    try {
      await api.assignShippingRoute({ request_ids: selectedForRoute, route_date: routeAssignDate || undefined });
      toast.success(`${selectedForRoute.length} solicitudes asignadas`);
      setSelectedForRoute([]);
      loadRequests();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleCreateRoute = async () => {
    if (!routeForm.scheduled_date) { toast.error('Fecha requerida'); return; }
    try {
      await api.createEnbixosRoute(routeForm);
      toast.success('Ruta creada');
      setRouteForm({ scheduled_date: '', scheduled_time: '', notes: '', branches_included: [], active: true });
      api.getEnbixosRoutes().then(setRoutes);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleSaveConfig = async () => {
    try {
      await api.updateEnbixosConfig(config);
      toast.success('Configuracion guardada');
      setConfigEditing(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const tabs = [
    { id: 'requests', label: 'Solicitudes', icon: Package },
    { id: 'routes', label: 'Rutas', icon: Route },
    { id: 'notifications', label: 'Notificaciones', icon: Bell, badge: notifications.unread_count },
  ];
  if (isAdmin) tabs.push({ id: 'config', label: 'Configuracion', icon: Settings });

  return (
    <div data-testid="enbixos-operator-page" className="max-w-5xl">
      <div className="mb-6">
        <h1 className="text-2xl font-light tracking-tight text-zinc-100 font-[Outfit]">
          <Truck className="w-6 h-6 inline mr-2 text-emerald-400" />
          Enbixos Logistics
        </h1>
        <p className="text-zinc-500 mt-1">Panel de operador logistico</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-white/5 pb-1">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm rounded-t-lg flex items-center gap-2 transition-colors ${tab === t.id ? 'bg-white/5 text-zinc-100' : 'text-zinc-500 hover:text-zinc-300'}`}>
            <t.icon className="w-4 h-4" />{t.label}
            {t.badge > 0 && <span className="px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-400 text-[10px]">{t.badge}</span>}
          </button>
        ))}
      </div>

      {/* === REQUESTS TAB === */}
      {tab === 'requests' && (
        <div>
          {/* Stats counters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Pendientes', value: stats.total_pending, color: 'text-zinc-400' },
              { label: 'Comprobantes', value: stats.total_uploaded, color: 'text-blue-400' },
              { label: 'Verificados', value: stats.total_verified, color: 'text-emerald-400' },
              { label: 'Sospechosos', value: stats.total_suspected, color: 'text-red-400' },
            ].map(s => (
              <div key={s.label} className="glass-card p-3 text-center">
                <p className={`text-lg font-semibold ${s.color}`}>{s.value || 0}</p>
                <p className="text-xs text-zinc-500">{s.label}</p>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4 items-center">
            <Select value={filter || 'all'} onValueChange={(v) => setFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-52 bg-white/5 border-white/10 text-zinc-100">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent className="bg-[#121215] border-white/10">
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(PAYMENT_STATUS_MAP).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedForRoute.length > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <Input type="date" value={routeAssignDate} onChange={e => setRouteAssignDate(e.target.value)}
                  className="bg-white/5 border-white/10 text-zinc-100 w-44" />
                <Button onClick={handleAssignRoute} className="bg-emerald-600 text-white" data-testid="assign-route-btn">
                  Asignar ruta ({selectedForRoute.length})
                </Button>
              </div>
            )}
          </div>

          {/* Request list */}
          <div className="space-y-2">
            {loading ? <p className="text-zinc-500 text-center py-8">Cargando...</p> :
             requests.length === 0 ? <p className="text-zinc-500 text-center py-8">Sin solicitudes</p> :
             requests.map(req => {
              const ps = PAYMENT_STATUS_MAP[req.payment_status] || PAYMENT_STATUS_MAP.pending_transfer;
              return (
                <div key={req.id} className="glass-card p-4 flex items-center gap-4" data-testid={`request-${req.id}`}>
                  <input type="checkbox" checked={selectedForRoute.includes(req.id)}
                    onChange={e => setSelectedForRoute(prev => e.target.checked ? [...prev, req.id] : prev.filter(x => x !== req.id))}
                    className="rounded border-white/20 bg-white/5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-zinc-100 text-sm font-medium truncate">{req.listing_title || 'Sin titulo'}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${ps.color}`}>{ps.label}</span>
                      {req.receipt_url && <FileText className="w-3.5 h-3.5 text-blue-400" title="Comprobante" />}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5 flex gap-3 flex-wrap">
                      <span>{req.sender_name}</span>
                      <span>{req.branch_origin} → {req.branch_destination}</span>
                      <span>{new Date(req.created_at).toLocaleDateString('es-MX')}</span>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => openDetail(req.id)} className="text-zinc-400">
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* === ROUTES TAB === */}
      {tab === 'routes' && (
        <div>
          <div className="glass-card p-6 mb-6">
            <h3 className="text-lg font-medium text-zinc-100 mb-4">Nueva ruta</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-zinc-400 mb-2 block">Fecha *</Label>
                <Input type="date" value={routeForm.scheduled_date} onChange={e => setRouteForm(f => ({ ...f, scheduled_date: e.target.value }))}
                  className="bg-white/5 border-white/10 text-zinc-100" data-testid="route-date-input" />
              </div>
              <div>
                <Label className="text-zinc-400 mb-2 block">Hora</Label>
                <Input type="time" value={routeForm.scheduled_time || ''} onChange={e => setRouteForm(f => ({ ...f, scheduled_time: e.target.value }))}
                  className="bg-white/5 border-white/10 text-zinc-100" />
              </div>
              <div>
                <Label className="text-zinc-400 mb-2 block">Notas</Label>
                <Input value={routeForm.notes || ''} onChange={e => setRouteForm(f => ({ ...f, notes: e.target.value }))}
                  className="bg-white/5 border-white/10 text-zinc-100" placeholder="Notas opcionales" />
              </div>
            </div>
            <Button onClick={handleCreateRoute} className="mt-4 bg-emerald-600 text-white" data-testid="create-route-btn">
              <Calendar className="w-4 h-4 mr-2" />Crear ruta
            </Button>
          </div>
          <div className="space-y-3">
            {routes.map(r => (
              <div key={r.id} className="glass-card p-4 flex items-center justify-between">
                <div>
                  <p className="text-zinc-100 font-medium">{r.scheduled_date} {r.scheduled_time || ''}</p>
                  <p className="text-xs text-zinc-500">{r.notes || 'Sin notas'}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${r.active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-zinc-500/10 text-zinc-500'}`}>
                  {r.active ? 'Activa' : 'Inactiva'}
                </span>
              </div>
            ))}
            {routes.length === 0 && <p className="text-zinc-500 text-center py-8">No hay rutas programadas</p>}
          </div>
        </div>
      )}

      {/* === NOTIFICATIONS TAB === */}
      {tab === 'notifications' && (
        <div className="space-y-2">
          {notifications.notifications?.length === 0 ? (
            <p className="text-zinc-500 text-center py-8">Sin notificaciones</p>
          ) : notifications.notifications?.map(n => (
            <div key={n.id} className={`glass-card p-4 flex items-center gap-3 ${!n.read ? 'border-l-2 border-emerald-500' : ''}`}>
              <Bell className={`w-4 h-4 ${!n.read ? 'text-emerald-400' : 'text-zinc-600'}`} />
              <div className="flex-1">
                <p className="text-sm text-zinc-200">{n.message}</p>
                <p className="text-xs text-zinc-500">{new Date(n.created_at).toLocaleString('es-MX')}</p>
              </div>
              {!n.read && (
                <Button size="sm" variant="ghost" onClick={async () => {
                  await api.markEnbixosNotifRead(n.id);
                  api.getEnbixosNotifications().then(setNotifications);
                }} className="text-zinc-400 text-xs">Marcar leida</Button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* === CONFIG TAB (Admin only) === */}
      {tab === 'config' && isAdmin && config && (
        <div className="glass-card p-6">
          <h3 className="text-lg font-medium text-zinc-100 mb-4">
            <DollarSign className="w-5 h-5 inline mr-2 text-emerald-400" />
            Configuracion de pago Enbixos
          </h3>
          <div className="space-y-4">
            {[
              { key: 'shipping_amount', label: 'Monto de guia ($MXN)', type: 'number' },
              { key: 'bank_name', label: 'Banco' },
              { key: 'account_holder', label: 'Titular' },
              { key: 'clabe', label: 'CLABE / Numero de cuenta' },
              { key: 'payment_instructions', label: 'Instrucciones de transferencia', textarea: true },
              { key: 'suggested_reference', label: 'Referencia sugerida' },
              { key: 'payment_notes', label: 'Notas de pago' },
            ].map(field => (
              <div key={field.key}>
                <Label className="text-zinc-400 mb-2 block">{field.label}</Label>
                {field.textarea ? (
                  <Textarea value={config[field.key] || ''} onChange={e => setConfig(c => ({ ...c, [field.key]: e.target.value }))}
                    className="bg-white/5 border-white/10 text-zinc-100" rows={2} data-testid={`config-${field.key}`} />
                ) : (
                  <Input type={field.type || 'text'} value={config[field.key] || ''} onChange={e => setConfig(c => ({ ...c, [field.key]: field.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
                    className="bg-white/5 border-white/10 text-zinc-100" data-testid={`config-${field.key}`} />
                )}
              </div>
            ))}
            <Button onClick={handleSaveConfig} className="bg-emerald-600 text-white" data-testid="save-config-btn">
              Guardar configuracion
            </Button>
          </div>
        </div>
      )}

      {/* === DETAIL DIALOG === */}
      <Dialog open={!!detailId} onOpenChange={() => { setDetailId(null); setDetail(null); }}>
        <DialogContent className="max-w-2xl bg-[#121215] border-white/10 text-zinc-100 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle de solicitud</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              {/* Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {[
                  ['Listing', detail.listing_title],
                  ['Comprador', detail.buyer_name],
                  ['Remitente', detail.sender_name],
                  ['PIMVS', detail.sender_pimvs || '-'],
                  ['Ciudad/Estado', detail.sender_city_state],
                  ['Telefono', detail.sender_phone],
                  ['Especie', detail.species_content],
                  ['Contenedor', detail.container_type || '-'],
                  ['Origen', detail.branch_origin],
                  ['Destino', detail.branch_destination],
                  ['Estado pago', PAYMENT_STATUS_MAP[detail.payment_status]?.label],
                  ['Fecha', new Date(detail.created_at).toLocaleDateString('es-MX')],
                ].map(([k, v]) => (
                  <div key={k}><span className="text-zinc-500">{k}:</span> <span className="text-zinc-200">{v || '-'}</span></div>
                ))}
              </div>

              {/* Receipt */}
              {detail.receipt_url && (
                <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/10">
                  <p className="text-sm text-blue-300 font-medium mb-1">Comprobante</p>
                  <a href={detail.receipt_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-400 underline">Ver comprobante</a>
                  <p className="text-xs text-zinc-500 mt-1">Subido: {detail.receipt_uploaded_at ? new Date(detail.receipt_uploaded_at).toLocaleString('es-MX') : '-'}</p>
                </div>
              )}

              {/* Status actions */}
              <div>
                <p className="text-sm text-zinc-400 mb-2">Cambiar estado:</p>
                <div className="flex flex-wrap gap-2">
                  {['under_review', 'payment_verified', 'payment_not_found', 'invalid_receipt', 'suspected_fraud', 'route_ready', 'shipped', 'canceled'].map(s => (
                    <Button key={s} size="sm" variant="outline"
                      className={`text-xs border-white/10 ${detail.payment_status === s ? 'bg-white/10' : ''}`}
                      onClick={() => handleStatusChange(detail.id, s)}
                      data-testid={`status-${s}`}>
                      {PAYMENT_STATUS_MAP[s]?.label || s}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Internal notes */}
              <div>
                <p className="text-sm text-zinc-400 mb-2">Notas internas:</p>
                <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                  {(detail.internal_notes || []).map((n) => (
                    <div key={n.at} className="text-xs p-2 rounded bg-white/5">
                      <span className="text-zinc-400">{n.by_name} · {new Date(n.at).toLocaleString('es-MX')}</span>
                      <p className="text-zinc-200 mt-0.5">{n.text}</p>
                    </div>
                  ))}
                  {(!detail.internal_notes || detail.internal_notes.length === 0) && (
                    <p className="text-xs text-zinc-600">Sin notas</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Agregar nota interna..."
                    className="bg-white/5 border-white/10 text-zinc-100 flex-1" data-testid="note-input" />
                  <Button onClick={handleAddNote} size="sm" className="bg-emerald-600 text-white" data-testid="add-note-btn">Agregar</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
